import { Component } from '@theme/component';

const VIEW_KEY = 'fifi-artist-view';

/**
 * Artist directory controls: category filters and an Image / List view switch.
 * The page works without it (image view, every artist shown); the chosen view is
 * remembered in this browser only.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [items] - Artist list items with data-groups="painters|photographers|"
 * @property {HTMLButtonElement[]} [filters] - Filter buttons with data-filter (empty = all)
 * @property {HTMLButtonElement[]} [views] - View buttons with data-view
 *
 * @extends {Component<Refs>}
 */
class FifiArtistDirectoryComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.toggleAttribute('data-enhanced', true);

    let saved = null;
    try {
      saved = window.localStorage.getItem(VIEW_KEY);
    } catch {
      saved = null;
    }
    if (saved === 'list' || saved === 'image') this.#applyView(saved);
  }

  /**
   * Shows only the artists in the chosen group (painters, photographers, makers).
   * @param {Event} event
   */
  filter(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const value = button.dataset.filter ?? '';
    for (const item of this.refs.items ?? []) {
      const groups = (item.dataset.groups ?? '').split('|');
      item.hidden = value !== '' && !groups.includes(value);
    }
    for (const filter of this.refs.filters ?? []) {
      filter.setAttribute('aria-pressed', String(filter === button));
    }
  }

  /**
   * Switches between the image grid and the list of names.
   * @param {Event} event
   */
  setView(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const view = button.dataset.view === 'list' ? 'list' : 'image';
    this.#applyView(view);
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Private browsing or blocked storage: the view simply is not remembered
    }
  }

  /** @param {'image' | 'list'} view */
  #applyView(view) {
    this.dataset.view = view;
    for (const button of this.refs.views ?? []) {
      button.setAttribute('aria-pressed', String(button.dataset.view === view));
    }
  }
}

if (!customElements.get('fifi-artist-directory-component')) {
  customElements.define('fifi-artist-directory-component', FifiArtistDirectoryComponent);
}
